import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const contactInfo = [
  {
    icon: Phone,
    label: "Naranbhai Mandabhai Solanki",
    value: "98986 73259",
    href: "tel:9898673259",
  },
  {
    icon: Phone,
    label: "Babubhai Mandabhai Solanki",
    value: "96620 59853",
    href: "tel:9662059853",
  },
  {
    icon: Phone,
    label: "Solanki Abhishekbhai Naranbhai",
    value: "98259 11774",
    href: "tel:9825911774",
  },
  {
    icon: Mail,
    label: "Email",
    value: "kishancoconutnursery@gmail.com",
    href: "mailto:kishancoconutnursery@gmail.com",
  },
];

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Inquiry sent successfully! We will contact you soon.");
      setFormData({ name: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send inquiry. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      "Hello, I am interested in Hybrid Coconut Seedlings from Kishan Coconut Nursery. Please provide more information."
    );
    window.open(`https://wa.me/919825911774?text=${message}`, "_blank");
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sm font-medium tracking-widest uppercase text-[#E07A5F] mb-4 block">
            Get In Touch
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A1C18] mb-6 tracking-tight"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Contact Us
          </h2>
          <p className="text-[#4A4F45] text-lg leading-relaxed">
            Ready to grow with quality seedlings? Reach out to us for inquiries
            and bulk orders.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info & Map */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
          >
            {/* Address Card */}
            <div className="bg-[#F9F8F4] p-8 rounded-3xl mb-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#2F5233] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3
                    className="font-semibold text-[#1A1C18] mb-2"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    Kishan Coconut Nursery
                  </h3>
                  <p className="text-[#4A4F45] leading-relaxed">
                    Village Supasi, Taluka Veraval<br />
                    District Gir Somnath – 362255<br />
                    Gujarat, India
                  </p>
                </div>
              </div>

              {/* Phone Numbers */}
              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <a
                    key={info.label}
                    href={info.href}
                    className="flex items-center gap-4 p-3 bg-white rounded-xl hover:bg-white/80 transition-colors"
                    data-testid={`contact-phone-${index}`}
                  >
                    <div className="w-10 h-10 bg-[#F0EFE9] rounded-xl flex items-center justify-center">
                      <info.icon className="w-5 h-5 text-[#2F5233]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#8C9187]">{info.label}</p>
                      <p className="font-medium text-[#1A1C18]">{info.value}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* WhatsApp CTA */}
            <Button
              onClick={openWhatsApp}
              className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full px-8 py-6 text-lg font-medium btn-scale flex items-center justify-center gap-3"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle className="w-6 h-6" />
              Chat on WhatsApp
            </Button>

            {/* Map */}
            <div className="mt-6 map-container rounded-3xl overflow-hidden h-[250px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3000!2d70.321722!3d20.986722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjDCsDU5JzEyLjIiTiA3MMKwMTknMTguMiJF!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Kishan Coconut Nursery Location - Village Supasi"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
          >
            <div className="bg-[#F9F8F4] p-8 md:p-12 rounded-3xl h-full">
              <h3
                className="text-2xl font-semibold text-[#1A1C18] mb-6"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Send an Inquiry
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#4A4F45] mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="bg-white border-[#E5E2D9] focus:border-[#2F5233] focus:ring-[#2F5233]/20 rounded-xl h-12 px-4"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A4F45] mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className="bg-white border-[#E5E2D9] focus:border-[#2F5233] focus:ring-[#2F5233]/20 rounded-xl h-12 px-4"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A4F45] mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements (quantity, delivery location, etc.)"
                    rows={5}
                    className="bg-white border-[#E5E2D9] focus:border-[#2F5233] focus:ring-[#2F5233]/20 rounded-xl px-4 py-3 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2F5233] hover:bg-[#1E3621] text-white rounded-full px-8 py-6 text-lg font-medium btn-scale shadow-lg shadow-[#2F5233]/20 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Inquiry
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
